// @system — Reusable landing pricing section: configurable tier cards.
// Extracted from the inline pricing markup that previously lived in the
// @system LandingPage so it can be reused across landing experiences.
// @custom — pass your own `plans` (and/or heading/subtitle) to brand it to a product.
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent } from '../Card'
import { info } from '@/config'

/** @typedef {import('@/config/@system/info').BrandPlan} BrandPlan */
/** @typedef {import('@/config/@system/text').LandingPlanText} LandingPlanText */

// Tiers come from the product only: the `plans` prop (product copy) or brand.json
// pricing.plans (info.pricing, the same source the /pricing page renders). With
// neither, the section renders nothing. The template ships no example tiers, so a
// product that mounts this section never sells plans its owner did not price.

/**
 * Map one brand.json pricing plan to a landing tier card.
 * @param {BrandPlan} p
 * @returns {LandingPlanText}
 */
export function brandPlanToLanding(p) {
  const isFree = p.plan === 'free'
  return {
    name: p.name,
    price: p.price || (isFree ? '$0' : ''),
    period: isFree ? 'forever' : (p.interval || '/month'),
    description: p.description || '',
    features: Array.isArray(p.features) ? p.features : [],
    cta: p.cta || (isFree ? 'Get Started Free' : 'Get Started'),
    ctaLink: isFree ? '/auth?tab=register' : `/auth?tab=register&plan=${p.plan}`,
    highlighted: Boolean(p.popular),
  }
}

/**
 * The product's brand.json pricing.plans as tier cards; empty when the brand has none.
 * Read at render time so a consumer sees the config in force when it mounts.
 * @returns {LandingPlanText[]}
 */
export function brandPlans() {
  return Array.isArray(info.pricing?.plans) ? info.pricing.plans.map(brandPlanToLanding) : []
}

/**
 * @typedef {object} PricingSectionProps
 * @property {LandingPlanText[]} [plans] Cards to render; absent or empty falls back to brand.json pricing.plans, then to no section
 * @property {string} [eyebrow]
 * @property {string} [heading]
 * @property {string} [subtitle]
 * @property {string} [highlightLabel] Badge on the highlighted plan
 */

/** @param {PricingSectionProps} props */
export function PricingSection({
  plans,
  eyebrow = 'Pricing',
  heading = 'Simple, transparent pricing',
  subtitle = 'No hidden fees. No surprise charges. Cancel anytime.',
  highlightLabel = 'Most Popular',
}) {
  const tiers = Array.isArray(plans) && plans.length > 0 ? plans : brandPlans()
  // No product plans: no section at all (never a bare header, never example tiers).
  if (tiers.length === 0) return null
  return (
    <section id="pricing" className="bg-brand-surface py-16 sm:py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-xs font-medium uppercase tracking-widest text-brand-text-muted/60 mb-3">
            {eyebrow}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            {heading}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-brand-text-muted max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
          {tiers.map((plan) => (
            <Card
              key={plan.name}
              className={`relative ${plan.highlighted ? 'border-primary shadow-lg ring-1 ring-primary/20' : 'border-brand-border'}`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-block rounded-full bg-brand-primary px-3 py-1 text-xs font-medium text-brand-text-on-primary">
                    {highlightLabel}
                  </span>
                </div>
              )}
              <CardContent className="pt-6 px-6 pb-6 flex flex-col">
                <div>
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  <p className="mt-1 text-sm text-brand-text-muted">{plan.description}</p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-bold">{plan.price}</span>
                    {plan.period && (
                      <span className="text-sm text-brand-text-muted">{plan.period}</span>
                    )}
                  </div>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className="h-4 w-4 text-brand-primary shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className="w-full mt-6"
                  variant={plan.highlighted ? 'default' : 'outline'}
                >
                  {plan.ctaLink.startsWith('mailto:') ? (
                    <a href={plan.ctaLink}>
                      {plan.cta}
                    </a>
                  ) : (
                    <Link to={plan.ctaLink}>
                      {plan.cta}
                    </Link>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}
